import React from "react";
import type { MessageInstance } from "antd/es/message/interface";
import type { MenuProps } from "antd";
import type { Dispatch } from "@reduxjs/toolkit";
import { Edit, Trash2, UserCheck } from "lucide-react";
import type { Staff } from "../types";
import { openPopup, PopupType, openStaffForm } from "../store";
import { useInviteStaffMutation, useUpdateStaffMutation } from "../api";
import { useMessage } from "../hooks/useMessage";

export const handleSuspendStaff = async (
  id: string,
  suspendStaffMutation: any,
  dispatch: Dispatch,
  message: MessageInstance,
): Promise<boolean> => {
  try {
    await suspendStaffMutation(id);
    message.success("Staff member suspended successfully");
    dispatch({ type: "ui/closePopup" } as any);
    return true;
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Failed to suspend staff member";
    message.error(errorMessage);
    return false;
  }
};

export const getActionMenu = (
  record: Staff,
  dispatch: Dispatch,
  PopupTypeEnum: typeof PopupType,
  isAdmin: boolean = false,
): MenuProps["items"] => {
  if (!isAdmin) {
    return [];
  }

  const menuItems: MenuProps["items"] = [
    {
      key: "edit",
      label: "Edit Role",
      icon: React.createElement(Edit, { size: 16 }),
      onClick: () => dispatch(openStaffForm(record.id)),
    },
  ];

  // Only show suspend for active staff
  if (record.status === "ACTIVE") {
    menuItems.push({
      key: "suspend",
      label: "Suspend",
      icon: React.createElement(Trash2, { size: 16 }),
      danger: true,
      onClick: () =>
        dispatch(
          openPopup({
            isOpen: PopupTypeEnum.SUSPEND_STAFF,
            id: record.id,
            title: "Suspend Staff Member",
            message: `Are you sure you want to suspend ${record.name}? They will no longer be able to access the system.`,
            data: { name: record.name },
          }),
        ),
    });
  }

  // Only show reactivate for suspended staff
  if (record.status === "SUSPENDED") {
    menuItems.push({
      key: "reactivate",
      label: "Reactivate",
      icon: React.createElement(UserCheck, { size: 16 }),
      onClick: () =>
        dispatch(
          openPopup({
            isOpen: PopupTypeEnum.REACTIVATE_STAFF,
            id: record.id,
            title: "Reactivate Staff Member",
            message: `Are you sure you want to reactivate ${record.name}? They will regain access to the system.`,
            data: { name: record.name },
          }),
        ),
    });
  }

  return menuItems;
};

export const useStaffSubmit = () => {
  const [inviteStaff] = useInviteStaffMutation();
  const [updateStaff] = useUpdateStaffMutation();
  const message = useMessage();

  const handleStaffSubmit = async (
    values: any,
    isEditing: boolean,
    staffId: string | null,
    setLoading: (loading: boolean) => void,
    onSuccess: () => void,
  ) => {
    setLoading(true);
    try {
      if (isEditing && staffId) {
        await updateStaff({
          id: staffId,
          data: {
            role: values.role.toUpperCase(),
          },
        }).unwrap();
        message.success("Staff member updated successfully");
      } else {
        await inviteStaff({
          email: values.email,
          role: values.role.toUpperCase(),
        }).unwrap();
        message.success("Staff invitation sent successfully");
      }
      onSuccess();
    } catch (error) {
      message.error(
        isEditing
          ? "Failed to update staff member"
          : "Failed to create staff member",
      );
    } finally {
      setLoading(false);
    }
  };

  return { handleStaffSubmit };
};
